import React from 'react';
import classNames from 'classnames';
import AnimateHeight from 'react-animate-height';
import Div from '../../Element/Div/Div';
import Flex from '../../Flex/Flex';
import Icon from '../../Icon/Icon';
import Headline from '../../Typography/Headline/Headline';
import Title from '../../Typography/Title/Title';
import styles from '../Accordion.module.scss';
import { CreateProps } from '../../../types/utils/CreateProps';
import BackgroundImage from '../../BackgroundImage/BackgroundImage';

type AccordionStatelessProps = CreateProps<
  {
    /** Visible heading in the Accordion */
    heading: string;
    /** Controls if the Accordion is initially expanded or not */
    initiallyExpanded?: boolean;
    /** Use regular or heading font */
    useHeadlineFontOnHeading?: boolean;
    /** Opens and closes the accordion */
    open?: boolean;
    /** Image rendered in the headline */
    imgSrc?: string;
    /** Image alt text */
    imgAlt?: string;
    /** Click event callback */
    onClick: (
      event:
        | React.KeyboardEvent<HTMLDivElement>
        | React.MouseEvent<HTMLDivElement, MouseEvent>
    ) => void;
  },
  typeof Div,
  'borderRadius' | 'backgroundColor' | 'hideOverflow'
>;

const AccordionStateless: React.FunctionComponent<AccordionStatelessProps> = ({
  heading,
  children,
  className,
  useHeadlineFontOnHeading = false,
  open,
  onClick,
  imgSrc,
  imgAlt,
  ...props
}) => {
  const height = open ? 'auto' : 0;

  return (
    <Div
      boxShadow={'sm'}
      borderRadius={{ size: 'md' }}
      backgroundColor="white"
      className={[className, styles.accordion]}
      hideOverflow
      {...props}
    >
      <Flex
        tabIndex={0}
        onKeyPress={(e: React.KeyboardEvent<HTMLDivElement>) => {
          e.key === 'Enter' && onClick(e);
        }}
        spaceBetween
        centeredVertically
        onClick={onClick}
        className={styles.accordion__header}
        hoverColor="purple500"
      >
        <Flex centeredVertically margin={{ right: 'xs' }}>
          {imgSrc && (
            <BackgroundImage
              src={imgSrc}
              alt={imgAlt as string}
              className={styles.accordion__image}
              scaleWithOriginalAspectRatio
            ></BackgroundImage>
          )}

          {useHeadlineFontOnHeading ? (
            <Headline size="xs">{heading}</Headline>
          ) : (
            <Title size="xs">{heading}</Title>
          )}
        </Flex>

        <Icon
          className={
            open ? styles.accordion__icon_rotate : styles.accordion__icon
          }
          name="chevron-down"
          size="md"
          color="black"
        />
      </Flex>
      <AnimateHeight
        duration={250}
        height={height}
        className={classNames(['expanded-content-test'])}
      >
        <Div className={styles.accordion__content__wrapper}>{children}</Div>
      </AnimateHeight>
    </Div>
  );
};

export default AccordionStateless;
